import type { RouteRecordRaw } from 'vue-router'
import { FilterMatchMode } from '@primevue/core/api'
import { generateFormKitSchema } from 'src/utils/form-kit-type'
import {
  useGetFetchAddress,
  useGetFetchAssignee,
  useGetFetchCategory,
  useGetFetchOrganisation,
  useGetFetchOrganisationContactPerson,
  useGetFetchServiceHistory,
  useGetFetchSite,
  useGetFetchSupplier,
  useGetFetchTicket,
  useGetFetchTicketContactPerson,
  useGetFetchUnit,
  useGetFetchUser,
  usePostCreateAddress,
  usePostCreateAssignee,
  usePostCreateServiceHistory,
  usePostFetchAllAddresses,
  usePostFetchAllAssignees,
  usePostFetchAllCategories,
  usePostFetchAllOrganisationContactPersons,
  usePostFetchAllOrganisations,
  usePostFetchAllServiceHistories,
  usePostFetchAllSites,
  usePostFetchAllSupplier,
  usePostFetchAllTicketContactPersons,
  usePostFetchAllTickets,
  usePostFetchAllUnits,
  usePostFetchAllUsers,
  usePutUpdateAssignee,
  usePutUpdateServiceHistory,
} from 'src/api/queries/order-portal-bff'
import {
  AssigneeFiltersDTO,
  CategoryFiltersDTO,
  CreateAddressDTO,
  CreateAssigneeDTO,
  CreateServiceHistoryDTO,
  FetchAddressResponse,
  FetchAssigneeResponse,
  FetchCategoryResponse,
  FetchOrganisationContactPersonResponse,
  FetchOrganisationResponse,
  FetchServiceHistoryResponse,
  FetchSiteResponse,
  FetchSupplierResponse,
  FetchTicketContactPersonResponse,
  FetchTicketResponse,
  FetchUnitResponse,
  FetchUserResponse,
  OrganisationFilterDTO,
  SiteFiltersDTO,
  TicketFiltersDTO,
  UnitFiltersDTO,
  UpdateAssigneeDTO,
  UpdateServiceHistoryDTO,
  UserFiltersDTO,
} from 'src/api/form/form-models'
import {
  postCreateAddressBody,
  postCreateAssigneeBody,
  postCreateServiceHistoryBody,
  putUpdateAssigneeBody,
  putUpdateServiceHistoryBody,
} from 'src/api/validations/zod'
import CrudView from 'src/views/CrudView.vue'
import CrudDetailsView from 'src/views/CrudDetailsView.vue'

const KNOWN_ENTITIES = new Set(['units', 'service-histories', 'tickets', 'organisations', 'suppliers', 'addresses', 'organisation-contact-persons', 'sites', 'ticket-contact-persons', 'users', 'assignees', 'categories'])

const crudUnitRoute: RouteRecordRaw = {
  path: '/units',
  name: 'units',
  component: CrudView,
  meta: {
    entityName: 'unit',
    tableColumns: FetchUnitResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllUnits,
      fetchOne: useGetFetchUnit,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('unit', FetchUnitResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
    filterFields: UnitFiltersDTO,
    searchQueryApiFields: ['description', 'fleet_number', 'serial_number', 'name'],
  },
}

const crudUnitDetailRoute: RouteRecordRaw = {
  path: '/units/:id',
  name: 'units-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'unit',
    tableColumns: FetchUnitResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllUnits,
      fetchOne: useGetFetchUnit,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('unit', FetchUnitResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudServiceHistoryRoute: RouteRecordRaw = {
  path: '/service-histories',
  name: 'service-histories',
  component: CrudView,
  meta: {
    entityName: 'service-history',
    tableColumns: FetchServiceHistoryResponse,
    crud: {
      create: true,
      update: true,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllServiceHistories,
      fetchOne: useGetFetchServiceHistory,
      create: usePostCreateServiceHistory,
      update: usePutUpdateServiceHistory,
    },
    validationSchema: {
      create: postCreateServiceHistoryBody,
      update: putUpdateServiceHistoryBody,
    },
    formSchema: {
      create: generateFormKitSchema('service-history', CreateServiceHistoryDTO, KNOWN_ENTITIES),
      update: generateFormKitSchema('service-history', UpdateServiceHistoryDTO, KNOWN_ENTITIES),
      view: generateFormKitSchema('service-history', FetchServiceHistoryResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
  },
}

const crudServiceHistoryDetailRoute: RouteRecordRaw = {
  path: '/service-histories/:id',
  name: 'service-histories-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'service-history',
    tableColumns: FetchServiceHistoryResponse,
    crud: {
      create: true,
      update: true,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllServiceHistories,
      fetchOne: useGetFetchServiceHistory,
      update: usePutUpdateServiceHistory,
    },
    validationSchema: {
      update: putUpdateServiceHistoryBody,
    },
    formSchema: {
      update: generateFormKitSchema('service-history', UpdateServiceHistoryDTO, KNOWN_ENTITIES),
      view: generateFormKitSchema('service-history', FetchServiceHistoryResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudTicketRoute: RouteRecordRaw = {
  path: '/tickets',
  name: 'tickets',
  component: CrudView,
  meta: {
    entityName: 'ticket',
    tableColumns: FetchTicketResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllTickets,
      fetchOne: useGetFetchTicket,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('ticket', FetchTicketResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
    filterFields: TicketFiltersDTO,
    tableColumnsDefault: [
      { name: 'priority' },
      { name: 'number' },
      { name: 'title' },
      { name: 'status' },
      { name: 'type' },
      { name: 'expected_complete_date' },
      { name: 'expected_duration' },
      { name: 'assignee' },
      { name: 'site' },
      { name: 'created_at' },
    ],
  },
}

const crudTicketDetailRoute: RouteRecordRaw = {
  path: '/tickets/:id',
  name: 'tickets-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'ticket',
    tableColumns: FetchTicketResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllTickets,
      fetchOne: useGetFetchTicket,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('ticket', FetchTicketResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudOrganisationRoute: RouteRecordRaw = {
  path: '/organisations',
  name: 'organisations',
  component: CrudView,
  meta: {
    entityName: 'organisation',
    tableColumns: FetchOrganisationResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllOrganisations,
      fetchOne: useGetFetchOrganisation,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('organisation', FetchOrganisationResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
    filterFields: OrganisationFilterDTO,
  },
}

const crudOrganisationDetailRoute: RouteRecordRaw = {
  path: '/organisations/:id',
  name: 'organisations-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'organisation',
    tableColumns: FetchOrganisationResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllOrganisations,
      fetchOne: useGetFetchOrganisation,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('organisation', FetchOrganisationResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudSupplierRoute: RouteRecordRaw = {
  path: '/suppliers',
  name: 'suppliers',
  component: CrudView,
  meta: {
    entityName: 'supplier',
    tableColumns: FetchSupplierResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllSupplier,
      fetchOne: useGetFetchSupplier,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('supplier', FetchSupplierResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
  },
}

const crudSupplierDetailRoute: RouteRecordRaw = {
  path: '/suppliers/:id',
  name: 'suppliers-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'supplier',
    tableColumns: FetchSupplierResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllSupplier,
      fetchOne: useGetFetchSupplier,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('supplier', FetchSupplierResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudAddressRoute: RouteRecordRaw = {
  path: '/addresses',
  name: 'addresses',
  component: CrudView,
  meta: {
    entityName: 'address',
    tableColumns: FetchAddressResponse,
    crud: {
      create: true,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllAddresses,
      fetchOne: useGetFetchAddress,
      create: usePostCreateAddress,
    },
    validationSchema: {
      create: postCreateAddressBody,
    },
    formSchema: {
      create: generateFormKitSchema('address', CreateAddressDTO, KNOWN_ENTITIES),
      view: generateFormKitSchema('address', FetchAddressResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
  },
}

const crudAddressDetailRoute: RouteRecordRaw = {
  path: '/addresses/:id',
  name: 'addresses-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'address',
    tableColumns: FetchAddressResponse,
    crud: {
      create: true,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllAddresses,
      fetchOne: useGetFetchAddress,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('address', FetchAddressResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudOrganisationContactPersonRoute: RouteRecordRaw = {
  path: '/organisation-contact-persons',
  name: 'organisation-contact-persons',
  component: CrudView,
  meta: {
    entityName: 'organisation-contact-person',
    tableColumns: FetchOrganisationContactPersonResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllOrganisationContactPersons,
      fetchOne: useGetFetchOrganisationContactPerson,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('organisation-contact-person', FetchOrganisationContactPersonResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
  },
}

const crudOrganisationContactPersonDetailRoute: RouteRecordRaw = {
  path: '/organisation-contact-persons/:id',
  name: 'organisation-contact-persons-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'organisation-contact-person',
    tableColumns: FetchOrganisationContactPersonResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllOrganisationContactPersons,
      fetchOne: useGetFetchOrganisationContactPerson,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('organisation-contact-person', FetchOrganisationContactPersonResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudSiteRoute: RouteRecordRaw = {
  path: '/sites',
  name: 'sites',
  component: CrudView,
  meta: {
    entityName: 'site',
    tableColumns: FetchSiteResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllSites,
      fetchOne: useGetFetchSite,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('site', FetchSiteResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
    filterFields: SiteFiltersDTO,
  },
}

const crudSiteDetailRoute: RouteRecordRaw = {
  path: '/sites/:id',
  name: 'sites-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'site',
    tableColumns: FetchSiteResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllSites,
      fetchOne: useGetFetchSite,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('site', FetchSiteResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudTicketContactPersonRoute: RouteRecordRaw = {
  path: '/ticket-contact-persons',
  name: 'ticket-contact-persons',
  component: CrudView,
  meta: {
    entityName: 'ticket-contact-person',
    tableColumns: FetchTicketContactPersonResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllTicketContactPersons,
      fetchOne: useGetFetchTicketContactPerson,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('ticket-contact-person', FetchTicketContactPersonResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
  },
}

const crudTicketContactPersonDetailRoute: RouteRecordRaw = {
  path: '/ticket-contact-persons/:id',
  name: 'ticket-contact-persons-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'ticket-contact-person',
    tableColumns: FetchTicketContactPersonResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllTicketContactPersons,
      fetchOne: useGetFetchTicketContactPerson,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('ticket-contact-person', FetchTicketContactPersonResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudUserRoute: RouteRecordRaw = {
  path: '/users',
  name: 'users',
  component: CrudView,
  meta: {
    entityName: 'user',
    tableColumns: FetchUserResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllUsers,
      fetchOne: useGetFetchUser,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('user', FetchUserResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
    filterFields: UserFiltersDTO,
  },
}

const crudUserDetailRoute: RouteRecordRaw = {
  path: '/users/:id',
  name: 'users-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'user',
    tableColumns: FetchUserResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllUsers,
      fetchOne: useGetFetchUser,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('user', FetchUserResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudAssigneeRoute: RouteRecordRaw = {
  path: '/assignees',
  name: 'assignees',
  component: CrudView,
  meta: {
    entityName: 'assignee',
    tableColumns: FetchAssigneeResponse,
    crud: {
      create: true,
      update: true,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllAssignees,
      fetchOne: useGetFetchAssignee,
      create: usePostCreateAssignee,
      update: usePutUpdateAssignee,
    },
    validationSchema: {
      create: postCreateAssigneeBody,
      update: putUpdateAssigneeBody,
    },
    formSchema: {
      create: generateFormKitSchema('assignee', CreateAssigneeDTO, KNOWN_ENTITIES),
      update: generateFormKitSchema('assignee', UpdateAssigneeDTO, KNOWN_ENTITIES),
      view: generateFormKitSchema('assignee', FetchAssigneeResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
    filterFields: AssigneeFiltersDTO,
  },
}

const crudAssigneeDetailRoute: RouteRecordRaw = {
  path: '/assignees/:id',
  name: 'assignees-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'assignee',
    tableColumns: FetchAssigneeResponse,
    crud: {
      create: true,
      update: true,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllAssignees,
      fetchOne: useGetFetchAssignee,
      update: usePutUpdateAssignee,
    },
    validationSchema: {
      update: putUpdateAssigneeBody,
    },
    formSchema: {
      update: generateFormKitSchema('assignee', UpdateAssigneeDTO, KNOWN_ENTITIES),
      view: generateFormKitSchema('assignee', FetchAssigneeResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

const crudCategoryRoute: RouteRecordRaw = {
  path: '/categories',
  name: 'categories',
  component: CrudView,
  meta: {
    entityName: 'category',
    tableColumns: FetchCategoryResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllCategories,
      fetchOne: useGetFetchCategory,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('category', FetchCategoryResponse, KNOWN_ENTITIES),
    },
    tableFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      created_at: { value: null, matchMode: FilterMatchMode.DATE_IS },
    },
    filterFields: CategoryFiltersDTO,
  },
}

const crudCategoryDetailRoute: RouteRecordRaw = {
  path: '/categories/:id',
  name: 'categories-detail',
  component: CrudDetailsView,
  meta: {
    entityName: 'category',
    tableColumns: FetchCategoryResponse,
    crud: {
      create: false,
      update: false,
      delete: false,
      details: true,
    },
    requests: {
      fetchAll: usePostFetchAllCategories,
      fetchOne: useGetFetchCategory,
    },
    validationSchema: {

    },
    formSchema: {
      view: generateFormKitSchema('category', FetchCategoryResponse, KNOWN_ENTITIES),
    },
    tableFilters: {},
  },
}

export const crudRoutes: RouteRecordRaw[] = [
  crudUnitRoute,
  crudUnitDetailRoute,
  crudServiceHistoryRoute,
  crudServiceHistoryDetailRoute,
  crudTicketRoute,
  crudTicketDetailRoute,
  crudOrganisationRoute,
  crudOrganisationDetailRoute,
  crudSupplierRoute,
  crudSupplierDetailRoute,
  crudAddressRoute,
  crudAddressDetailRoute,
  crudOrganisationContactPersonRoute,
  crudOrganisationContactPersonDetailRoute,
  crudSiteRoute,
  crudSiteDetailRoute,
  crudTicketContactPersonRoute,
  crudTicketContactPersonDetailRoute,
  crudUserRoute,
  crudUserDetailRoute,
  crudAssigneeRoute,
  crudAssigneeDetailRoute,
  crudCategoryRoute,
  crudCategoryDetailRoute,
]
